import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { FolderOpenIcon } from '@heroicons/react/24/solid';
import { type ChangeEvent, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { twMerge } from 'tailwind-merge';
import type { IGetResourceFilesServiceRes } from '@/api/Rover/ApplicationForm/types';
import SimpleDropDown, { DropDownItem } from '@/components/UI/SimpleDropDown';
import { wordsToCamelCase } from '@/utils';
import { resourceTableHeader } from '../../../../constants/Rover/Applications';
import ResourceCenterTableFooter from './ResourceCenterTableFooter';
import ResearchCenterTableRow from './ResourceCenterTableRow';

interface IResourceCenterTableProps {
  data: IGetResourceFilesServiceRes[];
  pageSize: number;
  page: number;
  perPage: number;
  perPageHandler: (e: ChangeEvent<HTMLSelectElement>) => void;
  pageChangeHandler: (action: string) => void;
  sortHandler: (sort: { order: string; orderBy: string }) => void;
}

export const ResourceCenterTable = ({
  data,
  page,
  pageSize,
  perPage,
  pageChangeHandler,
  perPageHandler,
  sortHandler,
}: IResourceCenterTableProps) => {
  const resourceCenterTableRef = useRef<HTMLDivElement | null>(null);
  const [searchParams] = useSearchParams();

  const tableHeader = [...resourceTableHeader];

  return (
    <div ref={resourceCenterTableRef} id='resource-center-table' className='rounded-md bg-white w-full'>
      <div className='w-full overflow-x-auto'>
        <table className='w-full'>
          <thead>
            <tr className='flex justify-between px-5 py-4 bg-gray-200'>
              {tableHeader.map((head, index) => (
                <th
                  key={index}
                  className={twMerge(['flex gap-2 text-neutralColor-normal text-left text-xs capitalize', head?.customClassName])}
                >
                  {head?.name}
                  {(head?.name === 'Name' || head?.name === 'Created At') && (
                    <SimpleDropDown
                      menuButtonClassName='flex text-sm items-start'
                      menuItemClassName='left-0 capitalize font-medium'
                      toggle={<ChevronDownIcon className='h-4 w-4' />}
                    >
                      <p className='px-3 pt-2'>Sort</p>
                      <DropDownItem
                        id='orderByAsc'
                        onClick={() =>
                          sortHandler({
                            orderBy: wordsToCamelCase(head.name),
                            order: 'asc',
                          })
                        }
                      >
                        <span>Ascending</span>
                      </DropDownItem>
                      <DropDownItem
                        id='orderByDesc'
                        onClick={() =>
                          sortHandler({
                            orderBy: wordsToCamelCase(head.name),
                            order: 'desc',
                          })
                        }
                      >
                        <span>Descending</span>
                      </DropDownItem>
                    </SimpleDropDown>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          {!searchParams.get('folder') && (
            <tbody className='divide-y'>
              {data?.map((row, index) => (
                <ResearchCenterTableRow key={row.id || index} row={row} />
              ))}
            </tbody>
          )}
        </table>
        {searchParams.get('folder') && (
          <div className='flex flex-col items-center w-full h-screen/3 justify-center py-4'>
            <FolderOpenIcon className='h-16 w-16 text-yellow-200' />
            <p className='font-semibold text-neutralColor-normal'>This folder is empty</p>
          </div>
        )}
        <ResourceCenterTableFooter
          pageSize={pageSize}
          page={page}
          perPage={perPage}
          perPageHandler={perPageHandler}
          pageChangeHandler={pageChangeHandler}
        />
      </div>
    </div>
  );
};
